import { IconAlertTriangle, IconClock, IconClockPlus, IconMinus } from '@tabler/icons-react'
import { Dayjs } from 'dayjs'
import { Fragment } from 'react/jsx-runtime'

import { add, AstraButton, AstraCallout, AstraCollapsibleCard, AstraIcon, AstraParagraph, AstraTooltip, DATE_TEMPLATES, format, subtract } from '~/astra'

import { DatePickerField } from '../../Form/DatePickerField'
import { getNestedFieldErrors } from '../../Form/formErrors'
import { withForm } from '../../Form/formHooks'
import { FieldGroupWrapper } from '../../Form/FormWrapper'
import { createFormFieldProps } from '../../Form/utils'
import { defaultFormValues, defaultServicePeriod } from './formState'
import { OpeningHoursField } from './OpeningHoursField'

export const LocationServicePeriodsFormFields = withForm({
    defaultValues: defaultFormValues,
    render: function Render({ form }) {
        const handleOnUpdateStartDate = (periodIndex: number, newDate: Dayjs | undefined) => {
            if (!newDate) return

            const previousIndex = periodIndex - 1
            if (previousIndex < 0) return
            const previousServicePeriod = form.getFieldValue('servicePeriods').at(previousIndex)
            if (!previousServicePeriod || previousServicePeriod.endDate) return
            form.setFieldValue(`servicePeriods[${previousIndex}].endDate`, subtract(newDate, 1, 'day'))
        }

        return (
            <form.Field name="servicePeriods">
                {servicePeriodsField => (
                    <div className="flex flex-col gap-3">
                        {servicePeriodsField.state.value.map((period, index, arr) => (
                            <Fragment key={index}>
                                <AstraCollapsibleCard
                                    header={
                                        <form.Subscribe selector={state => getNestedFieldErrors(state.errors, `servicePeriods[${index}]`)}>
                                            {errors => (
                                                <div className="flex items-center gap-2">
                                                    <AstraTooltip tooltipContent={errors.map(error => error.message).join('\n')} hidden={errors.length < 1}>
                                                        <AstraIcon
                                                            size="large"
                                                            backgroundColor={
                                                                errors.length > 0 ? 'bg-critical-rest' : index === 0 ? 'bg-info-subtle-rest' : 'bg-neutral-rest'
                                                            }
                                                            backgroundShape="rounded"
                                                            color={errors.length > 0 ? 'text-critical' : undefined}
                                                            icon={errors.length > 0 ? IconAlertTriangle : index === 0 ? IconClock : IconClockPlus}
                                                        />
                                                    </AstraTooltip>
                                                    <AstraParagraph>{index === 0 ? 'Nåværende åpningstider' : 'Fremtidige åpningstider'}</AstraParagraph>
                                                    {(period.startDate || period.endDate) && (
                                                        <AstraParagraph weight="regular" className="text-muted">
                                                            {period.startDate && `Fra ${format(period.startDate, DATE_TEMPLATES.norwegianDate)} `}
                                                            {period.endDate && `til ${format(period.endDate, DATE_TEMPLATES.norwegianDate)}`}
                                                        </AstraParagraph>
                                                    )}
                                                </div>
                                            )}
                                        </form.Subscribe>
                                    }
                                    defaultOpen={index === 0 || (index === arr.length - 1 && period.isNewlyAdded)}
                                >
                                    <div className="flex flex-col gap-4 py-2">
                                        <div className="flex gap-4">
                                            <FieldGroupWrapper className="flex-1">
                                                <form.Field
                                                    name={`servicePeriods[${index}].startDate`}
                                                    listeners={{
                                                        onChange: field => {
                                                            handleOnUpdateStartDate(index, field.value)
                                                        },
                                                    }}
                                                >
                                                    {field => (
                                                        <DatePickerField
                                                            label="Aktiv fra"
                                                            value={field.state.value}
                                                            buttonFullWidth={true}
                                                            {...createFormFieldProps(field, { blurOnChange: true, errorIgnoreTouched: true })}
                                                        />
                                                    )}
                                                </form.Field>
                                                <form.Field name={`servicePeriods[${index}].endDate`}>
                                                    {field => (
                                                        <DatePickerField
                                                            label="Aktiv til"
                                                            value={field.state.value}
                                                            buttonFullWidth={true}
                                                            startMonth={form.state.values.servicePeriods[index]?.startDate}
                                                            {...createFormFieldProps(field, { blurOnChange: true, errorIgnoreTouched: true })}
                                                        />
                                                    )}
                                                </form.Field>
                                            </FieldGroupWrapper>
                                            <div className="pt-5">
                                                <AstraButton
                                                    variant="secondary"
                                                    type="button"
                                                    icon={<AstraIcon icon={IconMinus} />}
                                                    onClick={() => servicePeriodsField.removeValue(index)}
                                                    disabled={index === 0}
                                                />
                                            </div>
                                        </div>
                                        <form.Subscribe selector={state => getNestedFieldErrors(state.errors, `servicePeriods[${index}].openingHours`)}>
                                            {errors => (
                                                <AstraCollapsibleCard
                                                    className=""
                                                    header={
                                                        <div className="flex items-center gap-2">
                                                            {errors.length > 0 && (
                                                                <AstraTooltip tooltipContent={errors.map(e => e.message).join('\n')}>
                                                                    <AstraIcon icon={IconAlertTriangle} color="text-critical" />
                                                                </AstraTooltip>
                                                            )}
                                                            Dager og klokkeslett
                                                        </div>
                                                    }
                                                    defaultOpen={index === 0 || period.isNewlyAdded}
                                                >
                                                    <OpeningHoursField form={form} fields={`servicePeriods[${index}]`} />
                                                </AstraCollapsibleCard>
                                            )}
                                        </form.Subscribe>
                                        {period.isNewlyAdded && period.endDate && Boolean(index === arr.length - 1) && (
                                            <AstraCallout
                                                color="default"
                                                text={
                                                    <div className="flex flex-col gap-2">
                                                        Hva skal de ordinære åpningstidene være etter denne perioden?
                                                        <div className="flex gap-2">
                                                            <AstraButton
                                                                variant="magic"
                                                                type="button"
                                                                label="Samme som før"
                                                                onClick={() => {
                                                                    if (!period.endDate) return
                                                                    const previousPermanentPeriod =
                                                                        form.getFieldValue('servicePeriods').findLast(sp => !sp.isNewlyAdded) ??
                                                                        defaultServicePeriod
                                                                    form.pushFieldValue('servicePeriods', {
                                                                        ...previousPermanentPeriod,
                                                                        endDate: undefined,
                                                                        startDate: add(period.endDate, 1, 'day'),
                                                                        originalServicePeriod: undefined,
                                                                    })
                                                                }}
                                                            />
                                                            <AstraButton
                                                                variant="secondary"
                                                                type="button"
                                                                label="Nye åpningstider"
                                                                onClick={() =>
                                                                    servicePeriodsField.pushValue({
                                                                        ...defaultServicePeriod,
                                                                        startDate: period.endDate ? add(period.endDate, 1, 'day') : undefined,
                                                                    })
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                }
                                            />
                                        )}
                                    </div>
                                </AstraCollapsibleCard>
                                {period.endDate && !period.isNewlyAdded && index === arr.length - 1 && (
                                    <AstraCallout
                                        color="caution"
                                        text="Siste periode har definert en sluttdato. Det er anbefalt at den siste perioden ikke har en sluttdato for å forhindre perioder uten definerte åpningstider. "
                                        action={{
                                            label: 'Fjern sluttdato for siste periode',
                                            onClick: () => servicePeriodsField.replaceValue(index, { ...period, endDate: undefined }),
                                        }}
                                    />
                                )}
                            </Fragment>
                        ))}
                    </div>
                )}
            </form.Field>
        )
    },
})
